import { useEffect, useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { useQuery } from 'react-query';

import { Button, Loader } from '@/components';
import useTapTourney from '@/hooks/useTapTourney';
import useToast from '@/hooks/useToast';
import { getRankingAvailable } from '@/services/tda';
import { ResponseRankingTda } from '@/types/tda';
import { cn } from '@/utils';

const ArrowDateSelector = ({ fechaCreacion }: { fechaCreacion: string }) => {
  const { tapTourneyData, updateTapProperties } = useTapTourney();
  const toast = useToast();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [stateTitle, setStateTitle] = useState('');

  const { data: dataSelectorData, isLoading } = useQuery(
    'DateSelectorDataTAP',
    async () => {
      if (tapTourneyData?.tourneyDate?.orden) {
        return await getRankingAvailable(tapTourneyData.tourneyDate.orden);
      }
      throw new Error('Datos necesarios no disponibles');
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (data) => {
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        }
      },
      onError: (error) => {
        toast.show((error as Error).message);
      },
    }
  );

  // Extraer y memoizar datos importantes
  const hasFechasRanking =
    (dataSelectorData as ResponseRankingTda)?.fechasConRanking?.length > 0;
  const fechasRanking = hasFechasRanking
    ? [
        ...((dataSelectorData as ResponseRankingTda)?.fechasConRanking ?? []),
      ].sort((a, b) => a.orden - b.orden)
    : [];

  // Calcular estados de botones
  const prevEnabled =
    currentIndex > 0 &&
    (tapTourneyData?.tourneyDate?.orden ?? 0) >
      (tapTourneyData?.tourneyDate?.ordenFC ?? 0);
  const nextEnabled = currentIndex < fechasRanking.length - 1;

  // Función para actualizar estado basado en el índice
  const updateStateFromIndex = (index: number) => {
    if (index >= 0 && index < fechasRanking.length) {
      const date = fechasRanking[index];
      setCurrentIndex(index);
      setStateTitle(date.nombre);

      updateTapProperties({
        tourneyDate: date,
      });
    }
  };

  const handlePrevMatch = () => {
    if (prevEnabled) {
      updateStateFromIndex(currentIndex - 1);
    }
  };

  const handleNextMatch = () => {
    if (nextEnabled) {
      updateStateFromIndex(currentIndex + 1);
    }
  };

  // Inicializar con el último elemento cuando los datos están disponibles
  useEffect(() => {
    if (fechasRanking.length > 0) {
      const lastIndex = fechasRanking.length - 1;
      updateStateFromIndex(lastIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechasRanking.length]);

  // Determinar el título a mostrar
  const displayTitle = hasFechasRanking ? stateTitle : fechaCreacion;

  return (
    <>
      <Loader isVisible={isLoading} timeout={true} />
      <div className="flex items-center bg-transparent lg:rounded-none lg:bg-gdt-blue-xl2 h-8 lg:h-[3.3rem] mb-4 lg:mb-0 justify-center mt-8 lg:mt-7">
        {hasFechasRanking && (
          <Button
            className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
              'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
                !prevEnabled,
            })}
            disabled={!prevEnabled}
            type="button"
            variant="primary"
            onClick={handlePrevMatch}
          >
            <IoIosArrowBack className="m-auto" size={19} />
          </Button>
        )}
        <p className="text-gdt-blue text-base lg:text-lg font-bold mx-5 min-w-[76px]">
          {displayTitle}
        </p>
        {hasFechasRanking && (
          <Button
            className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
              'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
                !nextEnabled,
            })}
            disabled={!nextEnabled}
            type="button"
            variant="primary"
            onClick={handleNextMatch}
          >
            <IoIosArrowForward className="m-auto" size={19} />
          </Button>
        )}
      </div>
    </>
  );
};

export default ArrowDateSelector;
